import React, { useContext } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import DirectionsIcon from '@material-ui/icons/Directions';
import HotelIcon from '@material-ui/icons/Hotel';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import { setSidebarTab, selectUI } from '../../store/slices/uiSlice';
import { selectCurrentRoute, selectSavedRoutes } from '../../store/slices/routesSlice';
import { t } from '../../i18n';
import { AuthContext } from '../../App';
import RouteForm from './RouteForm';
import VehicleDriverForm from './VehicleDriverForm';
import StopsList from './StopsList';
// eslint-disable-next-line no-unused-vars
import SavedRoutesList from './SavedRoutesList'; // legacy panel — kept for re-enable
import SavedRoutesQuickPanel from './SavedRoutesQuickPanel';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    background: 'transparent',
  },
  tabBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 38px 6px 8px', // right padding leaves room for the close button
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    borderBottom: '1px solid rgba(15,23,42,0.06)',
    flexShrink: 0,
  },
  tabPill: {
    flex: 1,
    minWidth: 0,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    padding: '0 8px',
    borderRadius: 8,
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: '0.68rem',
    fontWeight: 600,
    color: '#64748b',
    background: 'transparent',
    border: '1px solid transparent',
    transition: 'all 0.18s ease',
    position: 'relative',
    '&:hover': {
      background: 'rgba(15,23,42,0.04)',
      color: '#0f172a',
    },
  },
  tabPillActive: {
    background: 'linear-gradient(135deg, rgba(59,130,246,0.12) 0%, rgba(99,102,241,0.1) 100%)',
    border: '1px solid rgba(59,130,246,0.35)',
    color: '#2563eb',
    boxShadow: '0 1px 2px rgba(59,130,246,0.08), 0 0 0 2px rgba(59,130,246,0.06)',
    '&:hover': {
      background: 'linear-gradient(135deg, rgba(59,130,246,0.16) 0%, rgba(99,102,241,0.14) 100%)',
      color: '#2563eb',
    },
  },
  tabIcon: {
    fontSize: 14,
    flexShrink: 0,
  },
  tabLabel: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    letterSpacing: '0.01em',
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 16,
    height: 16,
    padding: '0 4px',
    borderRadius: 8,
    fontSize: '0.58rem',
    fontWeight: 800,
    lineHeight: 1,
    flexShrink: 0,
  },
  countBadgeIdle: {
    background: '#e2e8f0',
    color: '#475569',
  },
  countBadgeActive: {
    background: '#2563eb',
    color: '#fff',
    boxShadow: '0 1px 2px rgba(37,99,235,0.35)',
  },
  tabContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
}));

function SidebarPanel() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { activeSidebarTab, lang } = useSelector(selectUI);
  const route = useSelector(selectCurrentRoute);
  const savedRoutes = useSelector(selectSavedRoutes);
  const stopsCount = route.stops?.length || 0;
  const { modules, clientSettings } = useContext(AuthContext);

  // Stops tab visible whenever the route is currently using tacho.
  // RouteForm keeps `routeOptions.useTachograph` synced with the client
  // preference + vehicle type (with operator override on top), so we just
  // trust that boolean here. We no longer gate by the tachograph module —
  // the operator's intent (per-client setting OR per-route override) is
  // enough; legacy clients without the module on file still get the tab.
  const hasTachograph = route.routeOptions?.useTachograph === true;

  const tabs = [
    { key: 'route', Icon: DirectionsIcon, label: t(lang, 'tabRoute'), count: null, panel: <RouteForm /> },
  ];
  if (hasTachograph) {
    tabs.push({
      key: 'stops',
      Icon: HotelIcon,
      label: t(lang, 'tabStops'),
      count: stopsCount > 0 ? stopsCount : null,
      panel: <StopsList />,
    });
  }
  tabs.push({
    key: 'quick',
    Icon: BookmarkIcon,
    label: t(lang, 'tabSaved'),
    count: savedRoutes.length > 0 ? savedRoutes.length : null,
    panel: <SavedRoutesQuickPanel />,
  });
  // "Gravadas" (legacy full panel) — hidden but kept available for future use
  // To re-enable, uncomment the push below:
  // tabs.push({
  //   key: 'saved',
  //   icon: <BookmarkIcon style={{ fontSize: 16, marginBottom: 2 }} />,
  //   label: t(lang, 'tabSaved'),
  //   panel: <SavedRoutesList />,
  // });

  // Clamp tab index to valid range
  const safeTab = Math.min(activeSidebarTab, tabs.length - 1);

  return (
    <Box className={classes.root}>
      <Box className={classes.tabBar} role="tablist">
        {tabs.map((tab, idx) => {
          const active = idx === safeTab;
          const { Icon } = tab;
          return (
            <Box
              key={tab.key}
              role="tab"
              aria-selected={active}
              className={`${classes.tabPill} ${active ? classes.tabPillActive : ''}`}
              onClick={() => dispatch(setSidebarTab(idx))}
            >
              <Icon className={classes.tabIcon} />
              <span className={classes.tabLabel}>{tab.label}</span>
              {tab.count != null && (
                <span className={`${classes.countBadge} ${active ? classes.countBadgeActive : classes.countBadgeIdle}`}>
                  {tab.count}
                </span>
              )}
            </Box>
          );
        })}
      </Box>

      <Box className={classes.tabContent}>
        {tabs[safeTab]?.panel}
      </Box>
    </Box>
  );
}

export default SidebarPanel;
